import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Hero from './components/Hero';
import ThreatRadar from './components/ThreatRadar';
import ServiceMatrix from './components/ServiceMatrix';
import ContactForm from './components/ContactForm';
import Navigation from './components/Navigation';
import UnansweredQuestionsDashboard from './components/UnansweredQuestionsDashboard';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', sans-serif;
    background: #000000;
    color: #ffffff;
    overflow-x: hidden;
  }

  body::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(0, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(255, 0, 0, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: -1;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #111111;
  }

  ::-webkit-scrollbar-thumb {
    background: #00ffff;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #00cccc;
  }

  /* Selection styling */
  ::selection {
    background: #00ffff;
    color: #000000;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Orbitron', monospace;
    font-weight: 700;
  }

  /* Animation classes */
  .glow {
    text-shadow: 0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff;
  }

  .scan-line {
    position: relative;
    overflow: hidden;
  }

  .scan-line::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00ffff, transparent);
    animation: scan 2s linear infinite;
  }

  @keyframes scan {
    0% { left: -100%; }
    100% { left: 100%; }
  }

  .pulse {
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  position: relative;
`;

const MainContent = styled.main`
  position: relative;
  z-index: 1;
`;

const BackgroundGrid = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.3;
`;

function App() {
  // Check if we should show the dashboard (for admin access)
  const urlParams = new URLSearchParams(window.location.search);
  const showDashboard = urlParams.get('dashboard') === 'admin';

  if (showDashboard) {
    return (
      <>
        <GlobalStyle />
        <AppContainer>
          <BackgroundGrid />
          <UnansweredQuestionsDashboard />
        </AppContainer>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <BackgroundGrid />
        <Navigation />
        <MainContent>
          <div id="hero">
            <Hero />
          </div>
          <div id="threat-radar">
            <ThreatRadar />
          </div>
          <div id="services">
            <ServiceMatrix />
          </div>
          <div id="contact">
            <ContactForm />
          </div>
        </MainContent>
      </AppContainer>
    </>
  );
}

export default App; 